/**
 * One card's settings, rendered from its type's declared fields.
 *
 * **Only `select` so far.** A field kind is added when a card first needs one
 * — a case here and a row in the modules README; see `TODO.md`.
 *
 * The options load when the dialog opens, through `useAsync`. A stored value
 * that is no longer among them — a machine deleted, a permission lost — stays
 * selectable as "Not available", so opening the settings never changes a
 * card on its own.
 *
 * Saving here changes only the editor's copy. The dashboard's Save stores it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { useAsync } from '../../hooks/useAsync';
import { MODULE_NAMESPACES } from '../../modules/locales';
import type { DashboardConfig, DashboardContext, DashboardOption } from '../../modules/types';
import type { CatalogueEntry } from './layout';

export interface CardSettingsDialogProps {
  /** Null while closed. */
  card: { readonly entry: CatalogueEntry; readonly config: DashboardConfig } | null;
  ctx: DashboardContext;
  onSave: (config: DashboardConfig) => void;
  onClose: () => void;
}

export function CardSettingsDialog({ card, ctx, onSave, onClose }: CardSettingsDialogProps) {
  const { t } = useTranslation(['system', ...MODULE_NAMESPACES]);
  // The page remounts this per card (a `key`), so the first config is the only one.
  const [values, setValues] = useState<DashboardConfig>(() => card?.config ?? {});
  const fields = card?.entry.type.settings ?? [];

  const options = useAsync(
    () => Promise.all(fields.map(async (field) => field.options(ctx))),
    [card?.entry.key],
    { enabled: card !== null },
  );

  const labelOf = (option: DashboardOption): string => ('labelKey' in option ? t(option.labelKey) : option.label);
  const title = card ? t(card.entry.type.titleKey) : '';

  return (
    <FormDialog
      open={card !== null}
      title={t('system:dashboard.settingsTitle', { card: title })}
      disabled={options.data === undefined}
      maxWidth="xs"
      onSubmit={() => onSave(values)}
      onClose={onClose}
    >
      {options.error && <ErrorState error={options.error} onRetry={options.reload} />}
      {!options.error &&
        fields.map((field, index) => {
          const choices = options.data?.[index];
          if (!choices) return <Skeleton key={field.key} variant="rounded" height={56} />;

          const value = String(values[field.key] ?? '');
          const missing = value !== '' && !choices.some((option) => option.value === value);
          return (
            <TextField
              key={field.key}
              select
              label={t(field.labelKey)}
              value={value}
              onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
            >
              {choices.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {labelOf(option)}
                </MenuItem>
              ))}
              {missing && (
                <MenuItem value={value} disabled>
                  {t('system:dashboard.unavailable')}
                </MenuItem>
              )}
            </TextField>
          );
        })}
    </FormDialog>
  );
}
